// Shapes the workflow editor edits: a trigger node plus a tree of steps, each
// child gated by a condition on its parent's outcome (stored on the edge).
// graph.ts converts between these and the /workflows API.

import type { Edge, Node } from "@xyflow/react";
import {
  VAYKEN_DATA_SOURCE,
  PROSPECT_ORDER_TRIGGER,
  SCHEDULE_TRIGGER,
  SIGNAL_TRIGGER,
  type ScheduleTriggerConfig,
  type AgentContractKey,
  type AgentDefinition,
  type AgentOutputField,
  type DataSourceStepField,
  type FieldSpec,
  type SignalBriefVerdict,
  type SignalMatchMode,
  type WorkflowStepType,
  type WorkflowSubjectKind,
  type WorkflowTriggerType,
} from "../../../api/client.ts";

export type TriggerType = typeof SIGNAL_TRIGGER | typeof PROSPECT_ORDER_TRIGGER | typeof SCHEDULE_TRIGGER;
export const TRIGGER_TYPES: TriggerType[] = [SIGNAL_TRIGGER, PROSPECT_ORDER_TRIGGER, SCHEDULE_TRIGGER];

export function isTriggerType(value: string | null | undefined): value is TriggerType {
  return (TRIGGER_TYPES as string[]).includes(value ?? "");
}

export type StepKind =
  | "trigger"
  | "agent"
  | "add_to_prospect_list"
  | "slack_message"
  | "create_prospect_order"
  // Any step type the editor has no form of its own for: rendered from the
  // `config_fields` the step types endpoint declares.
  | "custom";
export type ActionKind = Exclude<StepKind, "trigger">;

// The trigger is not a stored step; its node always has this id.
export const TRIGGER_ID = "trigger";

// xyflow v12 requires node data to extend Record<string, unknown>.
//
// `base_config` keeps config keys the editor doesn't expose (e.g. an agent
// step's `max_turns` override) so saving doesn't drop them. `*_label`/`*_name` fields that
// only feed the canvas are display-only and never sent.
export interface SignalTriggerData extends Record<string, unknown> {
  kind: "trigger";
  trigger_type: typeof SIGNAL_TRIGGER;
  data_type: string;
  data_type_label: string;
  prompt: string;
  match_mode: SignalMatchMode;
  // The data type's own settings (its `config_fields`); {} for public data types.
  source: Record<string, unknown>;
}

export interface ProspectOrderTriggerData extends Record<string, unknown> {
  kind: "trigger";
  trigger_type: typeof PROSPECT_ORDER_TRIGGER;
}

export interface ScheduleTriggerData extends Record<string, unknown> {
  kind: "trigger";
  trigger_type: typeof SCHEDULE_TRIGGER;
  schedule: ScheduleTriggerConfig;
}

// A trigger type the editor has no form of its own for (`isTriggerType` is
// false): its settings are rendered from the `config_fields` the trigger types
// endpoint declares. `trigger_type` stays the editor's own marker so the
// bespoke triggers narrow cleanly; the real type is `custom_type`.
export const CUSTOM_TRIGGER = "custom";

export interface CustomTriggerData extends Record<string, unknown> {
  kind: "trigger";
  trigger_type: typeof CUSTOM_TRIGGER;
  custom_type: string;
  config: Record<string, unknown>;
}

export type TriggerData = SignalTriggerData | ProspectOrderTriggerData | ScheduleTriggerData | CustomTriggerData;

/** The trigger type the API knows the trigger by. */
export function apiTriggerType(data: TriggerData): string {
  return data.trigger_type === CUSTOM_TRIGGER ? data.custom_type : data.trigger_type;
}

// The editor's trigger type: a bespoke one, or the custom marker.
export type EditorTriggerType = TriggerType | typeof CUSTOM_TRIGGER;

/** The editor's trigger type for an API trigger type: unknown ones are custom. */
export function editorTriggerType(slug: string): EditorTriggerType {
  return isTriggerType(slug) ? slug : CUSTOM_TRIGGER;
}

/** "When a prospect order is placed": a trigger type as people read it. */
export function triggerTypeLabel(slug: string, triggerTypes?: WorkflowTriggerType[]): string {
  if (isTriggerType(slug)) return TRIGGER_META[slug].label;
  return triggerTypes?.find((t) => t.slug === slug)?.label ?? slug;
}

// What the agent step shows about the agent it runs. Display-only, filled in
// from the agent definitions list (which may land after the graph).
export interface AgentInfo {
  agent_name: string;
  // "" until the definition is known.
  contract: AgentContractKey | "";
  // The kinds of subject (signal, prospect order) its contract works on.
  contract_subject_kinds: string[];
  requires_instructions: boolean;
  output_fields: AgentOutputField[];
  // Non-implicit data sources the definition lets a step opt in to.
  allowed_data_sources: string[];
  vayken_tool_count: number;
}

export interface AgentData extends AgentInfo, Record<string, unknown> {
  kind: "agent";
  agent_id: string;
  // This step's own instructions, on top of the agent's.
  instructions: string;
  // Opted-in data sources with their per-run limits, by source key. Never
  // holds `vayken`: that one is implicit and only drawn.
  data_sources: Record<string, Record<string, unknown>>;
  // Claude model for this step; "" = the agent's own model.
  model: string;
  // Spend cap on signals and the orders made from them; null = the backend default.
  max_runs_per_day: number | null;
  enabled: boolean;
  base_config: Record<string, unknown>;
}

export interface ProspectListData extends Record<string, unknown> {
  kind: "add_to_prospect_list";
  collection_id: string;
  collection_name: string;
  enabled: boolean;
  base_config: Record<string, unknown>;
}

export interface SlackData extends Record<string, unknown> {
  kind: "slack_message";
  channel_id: string;
  channel_name: string;
  enabled: boolean;
  base_config: Record<string, unknown>;
}

// What the drafted order gets when the record doesn't say (the API's `defaults`);
// a brief field both leave blank comes from the tenant's company profile.
export interface OrderDefaults {
  requested_count: number | null;
}

export type OnRepeat = "update" | "new";

export interface ProspectOrderStepData extends Record<string, unknown> {
  kind: "create_prospect_order";
  // Hints for the drafter: how pages here phrase an order.
  instructions: string;
  defaults: OrderDefaults;
  assignee_user_ids: string[];
  // A later signal about the same page: redraft its open order, or make another.
  on_repeat: OnRepeat;
  // null = the backend default.
  max_orders_per_day: number | null;
  enabled: boolean;
  base_config: Record<string, unknown>;
}

// A step type the editor has no form of its own for; `config` is the step's
// whole config, edited through the type's `config_fields`.
export interface CustomStepData extends Record<string, unknown> {
  kind: "custom";
  step_type: string;
  config: Record<string, unknown>;
  enabled: boolean;
  base_config: Record<string, unknown>;
}

export type ActionData = AgentData | ProspectListData | SlackData | ProspectOrderStepData | CustomStepData;

/** The step type the API knows the step by. */
export function apiStepType(data: ActionData): string {
  return data.kind === "custom" ? data.step_type : data.kind;
}
export type StepData = TriggerData | ActionData;

export type StepNode = Node<StepData, "step">;

// A data source drawn next to an agent step. Derived from the agent node at
// render time, never part of the editor's state: `agent_node_id` + `source_key`
// say which agent's `data_sources` entry it mirrors.
export interface DataSourceNodeData extends Record<string, unknown> {
  kind: "data_source";
  source_key: string;
  agent_node_id: string;
  label: string;
  description: string;
  implicit: boolean;
  always_on: boolean;
  configured: boolean;
  spends_money: boolean;
  fields: DataSourceStepField[];
  config: Record<string, unknown>;
  vayken_tool_count: number;
}

export type DataSourceNode = Node<DataSourceNodeData, "data_source">;

export interface DataSourceEdgeData extends Record<string, unknown> {
  configured: boolean;
}

export type DataSourceEdge = Edge<DataSourceEdgeData, "data_source">;

export type EditorNode = StepNode | DataSourceNode;

export function isStepNode(node: EditorNode): node is StepNode {
  return node.type === "step";
}

export function isDataSourceNode(node: EditorNode): node is DataSourceNode {
  return node.type === "data_source";
}

export type FieldConditionOp = "eq" | "ne" | "gt" | "gte" | "lt" | "lte" | "in";

// When the child step runs, by its parent: under a signal trigger
// `min_rating` (the matcher's match strength); under an agent whatever its
// output contract allows (a signal brief: `verdicts` + `min_confidence`, the
// research agent's confidence in its verdict; a prospect list: `min_results`, a
// structured result: `field` + `op` + `value`, a text summary: nothing);
// under a prospect list or a prospect-order trigger nothing.
// `requires_approval` is not part of the condition: it is the child step's
// flag (saved as such), drawn on the edge because that is where it reads.
export interface ConditionData extends Record<string, unknown> {
  requires_approval?: boolean;
  min_rating?: number | null;
  verdicts?: SignalBriefVerdict[];
  min_confidence?: number | null;
  min_results?: number | null;
  field?: string | null;
  op?: FieldConditionOp;
  value?: unknown;
}

export type ConditionEdge = Edge<ConditionData, "condition">;

export type EditorEdge = ConditionEdge | DataSourceEdge;

export function isConditionEdge(edge: EditorEdge): edge is ConditionEdge {
  return edge.type === "condition";
}

export interface KindMeta {
  label: string;
  shortLabel: string;
  description: string;
  // MUI palette CSS variable used for the node's accent.
  accent: string;
  hasInput: boolean;
  // Whether steps can be chained under this one.
  hasOutput: boolean;
}

export const KIND_META: Record<StepKind, KindMeta> = {
  trigger: {
    label: "Trigger",
    shortLabel: "Trigger",
    description: "What starts the workflow.",
    accent: "var(--mui-palette-primary-main)",
    hasInput: false,
    hasOutput: true,
  },
  agent: {
    label: "Agent",
    shortLabel: "Agent",
    description:
      "Runs one of the platform's agents on what the trigger produced: research the signal, work the prospect order, or whatever the agent is defined to do.",
    accent: "var(--mui-palette-info-main)",
    hasInput: true,
    hasOutput: true,
  },
  add_to_prospect_list: {
    label: "Add to collection",
    shortLabel: "Collection",
    description: "Adds the companies to a prospect list.",
    accent: "var(--mui-palette-success-main)",
    hasInput: true,
    hasOutput: true,
  },
  slack_message: {
    label: "Send to Slack",
    shortLabel: "Slack",
    description: "Posts a summary to a Slack channel.",
    accent: "var(--mui-palette-error-main)",
    hasInput: true,
    hasOutput: false,
  },
  create_prospect_order: {
    label: "Create prospect order",
    shortLabel: "Prospect order",
    description:
      "Drafts a prospect order from what the signal's record asks for, and runs the steps below on that order: chain the prospecting agent here.",
    accent: "var(--mui-palette-warning-main)",
    hasInput: true,
    hasOutput: true,
  },
  // What a custom step shows until its type's info is known (`stepMeta`).
  custom: {
    label: "Step",
    shortLabel: "Step",
    description: "",
    accent: "var(--mui-palette-secondary-main)",
    hasInput: true,
    hasOutput: false,
  },
};

/** What a step's node says about it: the bespoke table, or the step type's info for a custom one. */
export function stepMeta(data: StepData, stepTypes?: WorkflowStepType[]): KindMeta {
  if (data.kind !== "custom") return KIND_META[data.kind];
  const type = stepTypes?.find((t) => t.slug === data.step_type);
  if (!type) return { ...KIND_META.custom, label: data.step_type, shortLabel: data.step_type };
  return {
    ...KIND_META.custom,
    label: type.label,
    shortLabel: type.label,
    description: type.description ?? "",
    hasOutput: type.supports_children,
  };
}

/** The settings a custom step declares; [] until the step types load. */
export function customStepFields(stepType: string, stepTypes?: WorkflowStepType[]): FieldSpec[] {
  return stepTypes?.find((t) => t.slug === stepType)?.config_fields ?? [];
}

/** The settings a custom trigger declares; [] until the trigger types load. */
export function customTriggerFields(triggerType: string, triggerTypes?: WorkflowTriggerType[]): FieldSpec[] {
  return triggerTypes?.find((t) => t.slug === triggerType)?.config_fields ?? [];
}

/** What fresh settings start with: each field's default, where it has one. */
export function fieldDefaultsOf(fields: FieldSpec[]): Record<string, unknown> {
  return Object.fromEntries(
    fields.filter((f) => f.default !== undefined && f.default !== null).map((f) => [f.name, f.default]),
  );
}

// Steps that make a new subject run the steps under them on it.
export const CHILD_SUBJECT: Partial<Record<StepKind, WorkflowSubjectKind>> = {
  create_prospect_order: PROSPECT_ORDER_TRIGGER,
};

/** The subject the steps under `data`'s step run on, when the step makes one. */
export function childSubjectKind(data: StepData, stepTypes?: WorkflowStepType[]): WorkflowSubjectKind | undefined {
  if (data.kind === "custom") {
    return stepTypes?.find((t) => t.slug === data.step_type)?.child_subject_kind ?? undefined;
  }
  return CHILD_SUBJECT[data.kind];
}

// What steps run on, as people read it.
const SUBJECT_LABELS: Record<string, string> = {
  signal: "signal",
  prospect_order: "prospect order",
  schedule: "scheduled run",
};

/** How people name a subject kind; one only the backend knows reads as itself. */
export function subjectLabel(kind: WorkflowSubjectKind): string {
  return SUBJECT_LABELS[kind] ?? kind.replace(/_/g, " ");
}

// Which subjects each step runs on: the step types endpoint is the source of
// truth, this the fallback while it loads (mirrors the step registry).
export const STEP_SUBJECTS: Record<ActionKind, WorkflowSubjectKind[]> = {
  agent: [SIGNAL_TRIGGER, PROSPECT_ORDER_TRIGGER, SCHEDULE_TRIGGER],
  add_to_prospect_list: [SIGNAL_TRIGGER, PROSPECT_ORDER_TRIGGER],
  slack_message: [SIGNAL_TRIGGER, PROSPECT_ORDER_TRIGGER, SCHEDULE_TRIGGER],
  create_prospect_order: [SIGNAL_TRIGGER],
  // Only the step types endpoint knows (`stepSubjects`).
  custom: [],
};

// What the trigger node says, per trigger type: when the workflow starts.
export const TRIGGER_META: Record<TriggerType, { label: string; description: string }> = {
  signal: {
    label: "When a record matches",
    description:
      "Starts when a new record of the data stream matches what you describe. Each match is a signal the steps below run on.",
  },
  prospect_order: {
    label: "When a prospect order is placed",
    description:
      "Starts when someone places a prospect order for this tenant. Re-run it from the order page.",
  },
  schedule: {
    label: "On a schedule",
    description:
      "Starts at the times you set. Each run is a scheduled run: the agent below works from its instructions, told when it last ran.",
  },
};

export function triggerLabel(data: TriggerData | undefined, triggerTypes?: WorkflowTriggerType[]): string {
  if (!data) return KIND_META.trigger.label;
  // "When a Notion page matches": the data stream says more than "a record".
  if (data.trigger_type === SIGNAL_TRIGGER && data.data_type_label) return `When a ${data.data_type_label} matches`;
  if (data.trigger_type === CUSTOM_TRIGGER) {
    return triggerTypes?.find((t) => t.slug === data.custom_type)?.label ?? data.custom_type;
  }
  return TRIGGER_META[data.trigger_type].label;
}

/** What the trigger node says it does. */
export function triggerDescription(data: TriggerData, triggerTypes?: WorkflowTriggerType[]): string {
  if (data.trigger_type === CUSTOM_TRIGGER) {
    return triggerTypes?.find((t) => t.slug === data.custom_type)?.description ?? "";
  }
  return TRIGGER_META[data.trigger_type].description;
}

// Steps the palette offers, in display order. The trigger is always present;
// which of these a workflow may use depends on its trigger (the step types
// endpoint says), but every kind must be listed here or the editor would drop
// it when loading a saved workflow.
export const PALETTE_KINDS: ActionKind[] = [
  "agent",
  "create_prospect_order",
  "add_to_prospect_list",
  "slack_message",
];

export function isActionKind(value: string): value is ActionKind {
  return (PALETTE_KINDS as string[]).includes(value);
}

export const DEFAULT_MAX_RUNS_PER_DAY = 20;
export const DEFAULT_VERDICTS: SignalBriefVerdict[] = ["verified", "qualified"];
// A root step plus four levels chained under it (the API's limit): room for a
// signal that creates an order, prospects it, finds people, lists and posts them.
export const MAX_STEP_DEPTH = 5;
export const DEFAULT_MAX_ORDERS_PER_DAY = 10;

export const CONTRACT_LABELS: Record<AgentContractKey, string> = {
  text: "Text summary",
  structured: "Structured result",
  prospect_list: "Prospect collection",
  signal_brief: "Signal brief",
  contact_list: "Contact collection",
};

// Contracts whose result is a count of list entries; a child condition is `min_results`.
export const LIST_CONTRACTS: ReadonlySet<AgentContractKey | ""> = new Set<AgentContractKey | "">([
  "prospect_list",
  "contact_list",
]);

export const NO_AGENT: AgentInfo = {
  agent_name: "",
  contract: "",
  contract_subject_kinds: [],
  requires_instructions: false,
  output_fields: [],
  allowed_data_sources: [],
  vayken_tool_count: 0,
};

/** What an agent step shows about `agent`; contracts say which subjects it runs on. */
export function agentInfo(
  agent: AgentDefinition | undefined,
  contracts: { key: string; subject_kinds: string[]; requires_step_instructions: boolean }[],
): AgentInfo {
  if (!agent) return NO_AGENT;
  const contract = contracts.find((c) => c.key === agent.output_contract);
  return {
    agent_name: agent.name,
    contract: agent.output_contract,
    contract_subject_kinds: contract?.subject_kinds ?? [],
    requires_instructions: contract?.requires_step_instructions ?? false,
    output_fields: agent.output_fields,
    allowed_data_sources: (agent.data_sources ?? []).filter((k) => k !== VAYKEN_DATA_SOURCE),
    vayken_tool_count: agent.vayken_tools.length,
  };
}

export const NO_ORDER_DEFAULTS: OrderDefaults = {
  requested_count: null,
};

export function defaultDataFor(kind: ActionKind): ActionData {
  switch (kind) {
    case "agent":
      return {
        kind,
        agent_id: "",
        ...NO_AGENT,
        instructions: "",
        data_sources: {},
        model: "",
        max_runs_per_day: null,
        enabled: true,
        base_config: {},
      };
    case "add_to_prospect_list":
      return { kind, collection_id: "", collection_name: "", enabled: true, base_config: {} };
    case "slack_message":
      return { kind, channel_id: "", channel_name: "", enabled: true, base_config: {} };
    case "create_prospect_order":
      return {
        kind,
        instructions: "",
        defaults: { ...NO_ORDER_DEFAULTS },
        assignee_user_ids: [],
        on_repeat: "update",
        max_orders_per_day: null,
        enabled: true,
        base_config: {},
      };
    case "custom":
      return customDataFor("", []);
  }
}

/** A fresh step of a type only the backend knows, starting from its fields' defaults. */
export function customDataFor(stepType: string, fields: FieldSpec[]): CustomStepData {
  return { kind: "custom", step_type: stepType, config: fieldDefaultsOf(fields), enabled: true, base_config: {} };
}

/** The condition a new edge out of `parent` starts with. */
export function defaultConditionFor(parent: StepData): ConditionData {
  switch (parent.kind) {
    case "trigger":
      return parent.trigger_type === SIGNAL_TRIGGER ? { min_rating: null } : {};
    case "agent":
      switch (parent.contract) {
        case "signal_brief":
          return { verdicts: [...DEFAULT_VERDICTS], min_confidence: null };
        case "prospect_list":
        case "contact_list":
          return { min_results: null };
        case "structured":
          return { field: null };
        default:
          return {};
      }
    default:
      return {};
  }
}

export function newStepId(): string {
  return crypto.randomUUID();
}
